import React, { useState, useEffect } from 'react'
import { useActivityType } from '../Hooks/ActivityTypeContext.js'
import PropTypes from 'prop-types'
import { setItem } from '../../../global/data/synch-storage.js'
import {
  regexValidationForUserId,
  isSsnFormat,
} from '../../../global/_utils.js'
import { getSSOInternalLoginPostUrl } from '../../../global/environments/config.js'
import InputGroup from '../../EmailSubscriptionCenter/SignIn/InputGroup.jsx'
import { fetchToken } from '../../SelfService/Data/SelfServiceDataSource.js'
import {
  login,
  postRiskAdvice,
  validateSSOUsername,
} from '../Data/LoginDatasource.js'
import {
  checkIfEmailIsInternal,
  checkIfSSO,
  setLogin,
  logOff,
} from '../../../global/authentication/authentication-manager.js'
import NewUser from '../Static/NewUser.jsx'
import { callProfileService } from '../Helpers/LoginProfile.js'
import {
  FORM_NAME_CHALLENGE_CHOICE,
  FORM_NAME_CHANGE_PASSWORD,
  FORM_NAME_CAPTURE_PHONE_NUMBER,
} from '../Helpers/LoginHelper.js'
import Loader from '../../Loading/Loading.jsx'
import FormNotification from '../../SelfService/FormNotification.jsx'
import { getUrlParameter } from '../../../global/data/url.js'
import { setSecurePageRedirect } from '../../../global/authentication/url-redirector.js'

let changePasswordMode = false
function LoginForm({
  handleDisplayChange,
  loginInputHook,
  usePasswordInputHook,
  loginState,
  tmx_id,
  websession_id,
}) {
  const RESPONSE_CODES = {
    SUCCESS_LOGIN: 'ALLOW',
    MFA_CHOICE: '3052',
    MFA_CHOICE_2: '3053',
    TEMPORARY_PASSWORD: '2016',
    CREDENTIALS_NOT_ACCEPTED: '7011-2006',
    SSO_LOCKED: '7011-2009',
    SSO_LOCKED_2: '7011-2010',
    BLOCKED_MFA: '7011-3001',
    LOCKED_MFA: '7011-3032',
    TEMP_PASSWORD_EXPIRED: '7011-2030',
    TEMP_PASSWORD_INCREASE_AUTH: 'INCREASEAUTH',
    PHONE_VERIFICATION_STATUS_INACTIVE: 'INACTIVE',
  }
  const setActType = useActivityType().setActivityType

  const [showPassword, setShowPassword] = useState(false)
  const [loginInput, setLoginInput] = loginInputHook
  const [passwordInput, setPasswordInput] = usePasswordInputHook

  const [formValidation, setFormValidation] = useState({
    'login-user-id': {
      error: false,
      errorMessage: 'Enter your firm email address',
    },
    'login-user-password': {
      error: false,
      errorMessage: 'Enter your password',
    },
  })

  const [loading, setLoading] = useState(false)
  const [formErrorFlag, setFormErrorFlag] = useState(false)
  const [formErrorMessage, setFormErrorMessage] = useState('')
  const [disableAllInputs, setDisableAllInputs] = useState(false)
  const [rememberLogin, setRememberLogin] = useState(
    !!window.localStorage.getItem('login')
  )

  const onPasswordPaste = (e) => {
    e.preventDefault()
    const text = e.clipboardData.getData('text').replace(/[\\"]/g, '')
    const { selectionStart = 0, selectionEnd = 0 } = e?.target
    const next =
      passwordInput?.slice(0, selectionStart) +
      text +
      passwordInput?.slice(selectionEnd)
    setPasswordInput(next)
  }

  useEffect(() => {
    if (loginState?.response) {
      const _handleState = async () => {
        let data = null
        const responseText = await loginState.response.text()
        if (responseText) {
          data = JSON.parse(responseText)
        }

        handleState(RESPONSE_CODES.SUCCESS_LOGIN, loginState.response, data)
      }
      _handleState()
    }

    if (loginState && loginState.newPassword) {
      changePasswordMode = false
      const _password = loginState?.newPassword?.replace(/[\\"]/g, '')
      setPasswordInput(_password)
      makeLoginCall(loginInput, _password)
    }
  }, [loginState])

  const validateUsername = async () => {
    try {
      const data = await validateSSOUsername(loginInput)
      if (data.error && data.error.details) {
        handleValidateResponse(data.error.details)
      } else {
        handleValidateResponse(data.value.status)
      }
    } catch (error) {
      showDefaultError()
    }
  }

  const handleValidateResponse = (response) => {
    switch (response.code) {
      case 'CTN_VALIDATE_USERID_1113': // BAU external id
        setFormErrorFlag(true)
        setFormErrorMessage(
          'Please <a href="/register">re-register</a> to update your User ID. We\'ve made changes to our website since your last login. Once you re-register, your new User ID will be the firm email address you provide. Thank you.'
        )
        break
      case 'CTN_VALIDATE_USERID_1115': // investor
        setFormErrorFlag(true)
        setFormErrorMessage(
          'Individual investors:  <a href="/web/columbia/landing/account-access/login">Log in</a> to our Individual Investors website to access your accounts.'
        )
        break
      case 'CTN_VALIDATE_USERID_2000': // BAU internal id
      case 'CTN_VALIDATE_USERID_2001': // Bravos Internal First time login
      case 'CTN_VALIDATE_USERID_2002': // Bravos Internal (not logging in for first time)
        redirectToInternalSSO()
        break
      case 'CTN_VALIDATE_USERID_2003': // Bravos external
        setShowPassword(true)
        break
      default:
        showDefaultError()
    }
  }

  const showDefaultError = () => {
    setFormErrorFlag(true)
    setFormErrorMessage(
      'A problem occured with your account. Please call us at <a aria-label="800. 2 4 8. 2 8 2 8" href="tel: 8002482828">800.248.2828</a> or check your email for further instruction.'
    )
  }

  const handleNextClicked = () => {
    const redirectUrl = getUrlParameter('redirect')
    if (redirectUrl) {
      // only relative urls, we cannot let this go to the outside world
      if (redirectUrl.indexOf('/') === 0) {
        setSecurePageRedirect(redirectUrl)
      } else {
        try {
          const url = new URL(redirectUrl)
          setSecurePageRedirect(url.pathname + url.search)
        } catch (error) {
          console.log('error converting url', redirectUrl)
        }
      }
    }

    let siteminderUrl = getUrlParameter('TARGET')
    //not the shopping store. site minder puts this in the query when it hits a protected url
    if (siteminderUrl) {
      //clean out weird random stuff on the url from siteminder
      siteminderUrl = siteminderUrl.replace('$SM$', '')
      siteminderUrl = siteminderUrl.replaceAll('/ctncms.svc/secure', '/api')
      siteminderUrl = decodeURIComponent(siteminderUrl)

      try {
        const url = new URL(siteminderUrl)
        setSecurePageRedirect(url.pathname + url.search)
      } catch (error) {
        console.log('error converting url', redirectUrl)
      }
    }
    const isValidUserId = regexValidationForUserId(loginInput)
    if (!loginInput) {
      const _formValidation = { ...formValidation }
      _formValidation['login-user-id'].error = true
      setFormValidation(_formValidation)
    } else if (isSsnFormat(loginInput)) {
      const _formValidation = { ...formValidation }
      _formValidation['login-user-id'].error = true
      _formValidation['login-user-id'].errorMessage =
        'The User ID you entered is incorrect. Please check and try again.'
      setFormValidation(_formValidation)
    } else if (!isValidUserId?.isValid) {
      const _formValidation = { ...formValidation }
      _formValidation['login-user-id'].error = true
      _formValidation['login-user-id'].errorMessage = isValidUserId?.message
      setFormValidation(_formValidation)
    } else if (checkIfSSO(loginInput)) {
      validateUsername()
    } else if (checkIfEmailIsInternal(loginInput)) {
      //   //$('#login-user-id').val('');
      redirectToInternalSSO()
    } else {
      setShowPassword(true)
    }
  }

  const handleLoginClicked = async () => {
    // if we detect that the format of the user is not an email (no @ sign)
    // then we will redirect them to the internal SSO login page
    if (isSsnFormat(loginInput)) {
      const _formValidation = { ...formValidation }
      _formValidation['login-user-id'].error = true
      _formValidation['login-user-id'].errorMessage =
        'The User ID you entered is incorrect. Please check and try again.'
      setFormValidation(_formValidation)
      return
    }
    if (checkIfEmailIsInternal(loginInput)) {
      validateUsername()
      return
    }

    if (!passwordInput) {
      const _formValidation = { ...formValidation }
      _formValidation['login-user-password'].error = true
      _formValidation['login-user-password'].errorMessage =
        'Enter your password'
      setFormValidation(_formValidation)
      return
    }

    try {
      makeLoginCall(loginInput, passwordInput, tmx_id)
    } catch (error) {
      showDefaultError()
    }
  }

  const makeLoginCall = async (username, password, tmx_id) => {
    try {
      setLoading(true)

      const tokenResponseData = await fetchToken()
      const apiToken = tokenResponseData.headers.get('apitoken')
      setItem('apiToken', apiToken)

      const response = await login(username, password, websession_id, tmx_id)
      const actType = response.headers.get('act_type')
      actType && setActType(actType)

      handleLoginSuccessResponse(response)
    } catch (error) {
      setLoading(false)
      showDefaultError()
    }
  }

  const handleLoginSuccessResponse = async (response) => {
    const data = await response?.json()
    setLoading(false)

    if (data) {
      if (data.value?.phnVrfStatus === 'INACTIVE') {
        handleState(data.value.phnVrfStatus, response, data)
      } else if (data.value && data.value.riskAdvice) {
        if (data.value.riskAdvice === 'INCREASEAUTH') {
          handleState(data.value.reasonCode, response, data)
        } else {
          handleState(data.value.riskAdvice, response, data)
        }
      } else if (data.value && data.value.reasonCode) {
        handleState(data.value && data.value.reasonCode, response, data)
      } else if (data.error && data.error.details) {
        handleState(data.error.details.code, response, data)
      } else {
        showDefaultError()
      }
    } else {
      showDefaultError()
    }
  }

  const tempPasswordRiskAdvice = async () => {
    const riskData = {
      deviceSignature: window?.getDeviceSignature(),
    }
    const response = await postRiskAdvice(riskData)
    const data = await response.json()
    setActType('riskAdvice')

    // flaky guessing. do i get one of these codes back from this response?
    if (data.value) {
      if (data.value.riskAdvice) {
        handleState(data.value.riskAdvice, response, data)
      } else if (data.value.reasonCode) {
        handleState(data.value.reasonCode, response, data)
      } else if (data.error && data.error.details && data.error.details.code) {
        handleState(data.error.details.code, response, data)
      } else if (data.errorCode) {
        handleState(data.errorCode, response, data)
      } else {
        showDefaultError()
      }
    } else {
      showDefaultError()
    }
  }

  const handleState = async (code, response, data) => {
    const header = response.headers

    if (header) {
      // lets set the api token if i find it??
      const apiToken = header.get('apitoken')
      if (apiToken) {
        setItem('apiToken', apiToken)
      }

      // also always set the deviceid if you find it
      const deviceId = header.get('deviceid')
      if (deviceId && window.setdevIdInLocalStore) {
        window.setdevIdInLocalStore(deviceId)
      }
    }

    switch (code) {
      case RESPONSE_CODES.SUCCESS_LOGIN:
        if (changePasswordMode) {
          handleDisplayChange(FORM_NAME_CHANGE_PASSWORD, {
            currentPassword: passwordInput,
          })
          break
        }

        setLoginState(response)
        break
      case RESPONSE_CODES.PHONE_VERIFICATION_STATUS_INACTIVE:
        handleDisplayChange(
          FORM_NAME_CAPTURE_PHONE_NUMBER,
          data.value?.userVrfPhoneNbr
        )
        break
      case RESPONSE_CODES.TEMP_PASSWORD_INCREASE_AUTH:
        handleDisplayChange(
          FORM_NAME_CHALLENGE_CHOICE,
          data,
          changePasswordMode ? 'temp-increaseauth' : 'increaseauth'
        )
        break
      case RESPONSE_CODES.MFA_CHOICE:
      case RESPONSE_CODES.MFA_CHOICE_2:
        handleDisplayChange(FORM_NAME_CHALLENGE_CHOICE, data, 'increaseauth')
        break
      case RESPONSE_CODES.TEMPORARY_PASSWORD:
        changePasswordMode = true
        tempPasswordRiskAdvice()
        break
      case RESPONSE_CODES.CREDENTIALS_NOT_ACCEPTED:
        setFormValidation({
          ...formValidation,
          'login-user-password': {
            ...formValidation['login-user-password'],
            error: true,
            errorMessage:
              'The User ID and/or password you entered is incorrect. Please try again.',
          },
        })
        break
      case RESPONSE_CODES.TEMP_PASSWORD_EXPIRED:
        setFormErrorFlag(true)
        setFormErrorMessage(
          'The temporary password you entered has expired. Please call a Columbia Threadneedle representative at <a aria-label="800. 2 4 8. 2 8 2 8" href="tel: 8002482828">800.248.2828</a> for assistance.'
        )
        break
      case RESPONSE_CODES.SSO_LOCKED:
      case RESPONSE_CODES.SSO_LOCKED_2:
      case RESPONSE_CODES.BLOCKED_MFA:
      case RESPONSE_CODES.LOCKED_MFA:
        setFormErrorFlag(true)
        setFormErrorMessage(
          'For your protection, your account has been temporarily locked due to several failed login attempts. Please call a Columbia Threadneedle representative at <a aria-label="800. 2 4 8. 2 8 2 8" href="tel: 8002482828">800.248.2828</a> for assistance.'
        )
        setDisableAllInputs(true)
        break
      default:
        showDefaultError()
    }
  }

  const setLoginState = async (response) => {
    const apiToken = response.headers.get('apitoken')
    setItem('apiToken', apiToken)

    if (window.location.hostname.indexOf('local' >= 0)) {
      const cmsessionValue = response.headers.get('CSMSESSION')
      setLogin(loginInput, null, cmsessionValue)
    } else {
      setLogin(loginInput, null)
    }

    if (rememberLogin) {
      window.localStorage.setItem('login', loginInput)
    } else {
      window.localStorage.removeItem('login')
    }

    setLoading(true)
    await callProfileService(handleProfileErrorResponse)
    setLoading(false)
  }

  const handleProfileErrorResponse = (error) => {
    if (error) {
      logOff()
      const _formValidation = { ...formValidation }
      _formValidation['login-user-password'].error = true
      _formValidation['login-user-password'].errorMessage =
        "We're unable to log you in. Please see the above message for details."
      setFormValidation(_formValidation)

      if (error.details && error.details.code === 'CTN_PROFILE_1113') {
        // disqualified by we manually kicked you out of the cool kids club
        setFormErrorFlag(true)
        setFormErrorMessage(
          'Unfortunately, your guest access has expired  because we\'ve determined you are not eligible for site access. Please call a Columbia Threadneedle representative at <a aria-label="800. 2 4 8. 2 8 2 8" href="tel: 8002482828">800.248.2828</a> for assistance.'
        )
      } else if (error.details && error.details.code === 'CTN_PROFILE_1117') {
        // disqualied by timeout
        setFormErrorFlag(true)
        setFormErrorMessage(
          'Unfortunately, your guest access to this site has expired because we were unable to validate your registration information. Please call a Columbia Threadneedle representative at <a aria-label="800. 2 4 8. 2 8 2 8" href="tel: 8002482828">800.248.2828</a> for assistance.'
        )
      } else {
        showDefaultError()
      }
    }
  }

  const redirectToInternalSSO = () => {
    window.location.href = getSSOInternalLoginPostUrl(loginInput)
  }

  const handleLoginInputChange = (event) => {
    const _formValidation = { ...formValidation }
    _formValidation['login-user-id'].error = false
    setFormValidation(_formValidation)
    setLoginInput(event.target.value)
  }

  const handlePasswordInputChange = (event) => {
    setPasswordInput(event?.target?.value?.replace(/[\\"]/g, ''))
  }

  const handlePasswordInputOnKeyUp = (event) => {
    if (event.key === 'Enter') {
      handleLoginClicked()
    }
  }

  const onRemeberLoginChecked = (event) => {
    setRememberLogin(event.target.checked)
  }

  return (
    <div data-analytics-login-step="Log In">
      {loading && <Loader />}
      <div className="Grid">
        <div className="u-sizeFull u-lg-size5of12 u-lg-after1of12 u-marginBottom6gu Content">
          {formErrorFlag && (
            <FormNotification content={formErrorMessage} isHtml={true} />
          )}
          <h2 className="u-marginBottom2gu u-marginTop0">Log In</h2>
          <p className="u-marginBottom3gu">
            Not an advisor? Log in as an{' '}
            <a href="/web/columbia/landing/account-access/login">individual</a>{' '}
            or <a href="/institutional/login">institutional</a> investor.
          </p>

          <fieldset>
            <InputGroup
              handleInputChange={handleLoginInputChange}
              formValidation={formValidation}
              fieldName="login-user-id"
              label="User ID"
              fieldId="login-user-id"
              placeholder="Firm Email Address or SSO User ID"
              compact={false}
              disabled={disableAllInputs}
              defaultValue={loginInput}
            />

            {showPassword && (
              <InputGroup
                type="password"
                handleInputChange={handlePasswordInputChange}
                formValidation={formValidation}
                fieldName="login-user-password"
                label="User password"
                fieldId="login-user-password"
                placeholder="Password"
                compact={false}
                disabled={disableAllInputs}
                onKeyUp={handlePasswordInputOnKeyUp}
                maskedValue={passwordInput}
                onPaste={onPasswordPaste}
              />
            )}

            {!showPassword && (
              <button
                onClick={handleNextClicked}
                className="Button Button--primary Button--block u-marginBottom4gu u-textNoTransform"
                data-analytics="login-next"
              >
                Next
              </button>
            )}

            {showPassword && (
              <button
                disabled={disableAllInputs}
                onClick={handleLoginClicked}
                className="Button Button--primary Button--block u-marginBottom4gu"
                data-analytics="login"
              >
                Log in
              </button>
            )}

            <div className="Checkbox u-marginBottom3gu">
              <input
                defaultChecked={rememberLogin}
                onChange={onRemeberLoginChecked}
                disabled={disableAllInputs}
                className="Checkbox-button u-bgColorWhite"
                id="remember-user-id"
                type="checkbox"
              />
              <label htmlFor="remember-user-id" className="Checkbox-label">
                Remember User ID
              </label>
            </div>
            <a
              href="/register/forgot-password/"
              className="Link"
              data-analytics="login-forgot-password"
            >
              Forgot password?
            </a>
          </fieldset>
        </div>
        <div className="u-sizeFull u-lg-before1of12 u-lg-size5of12 u-marginBottom6gu Content">
          <NewUser />
        </div>
      </div>
    </div>
  )
}

LoginForm.propTypes = {
  handleDisplayChange: PropTypes.func,
  loginInputHook: PropTypes.array,
  loginState: PropTypes.object,
  usePasswordInputHook: PropTypes.array,
}

export default LoginForm
